import type { ComposerInputIntent, ComposerInputProjection } from '../../projection/composer/contracts';

export type InputResponse = { inputRequest: Record<string, unknown> } & (
  | { values: Array<{ field_key: string; value: unknown }>; response_kind?: 'answers'; message?: never }
  | { response_kind: 'message'; message: string; values?: never }
);
const inputError = () => Object.assign(new Error('当前问题已变化，请刷新后重试。'), { code: 'input_request_stale' });
const textValues = ({ projection, content }: ComposerInputIntent) => {
  if (typeof content !== 'string' || !content.trim() || !projection?.fieldKey) throw inputError();
  return [{ field_key: projection.fieldKey, value: content.trim() }];
};
const choiceValues = ({ projection, actionInputValues }: ComposerInputIntent) => {
  const fields = projection?.inputRequest?.missing_inputs;
  if (!Array.isArray(fields) || !Array.isArray(actionInputValues) || fields.length !== actionInputValues.length
    || actionInputValues.some((entry, index) => entry.fieldKey !== fields[index])) throw inputError();
  return actionInputValues.map(({ fieldKey, value }) => ({ field_key: fieldKey, value }));
};
const VALUE_BUILDERS = new Map<string, (input: ComposerInputIntent) => InputResponse>([
  ['text', (input) => ({ inputRequest: input.projection!.inputRequest!, values: textValues(input) })],
  ['chooser', (input) => ({ inputRequest: input.projection!.inputRequest!, values: choiceValues(input) })],
  ['message', ({ projection, content, actionInputValues }) => {
    if (projection?.protocol !== 'auto' || typeof content !== 'string' || !content.trim()
      || actionInputValues !== undefined) throw inputError();
    return { inputRequest: projection.inputRequest!, response_kind: 'message', message: content };
  }],
]);

/** UI values become an explicit command only after the current owner has been resolved. */
export const buildComposerInputSubmission = (input: ComposerInputIntent): InputResponse => {
  const request = input.projection?.inputRequest;
  const build = VALUE_BUILDERS.get(input.projection?.kind ?? '');
  if (!request || !build) throw input.projection?.error ?? inputError();
  return structuredClone(build(input));
};

/** Client lifecycle validation, not backend readiness/admission policy. */
export const resolveInputInteraction = ({ ready, busy, projection }: {
  ready: boolean; busy: boolean; projection?: ComposerInputProjection;
}) => {
  const refusal = [
    { blocked: !ready, reason: 'connection_unavailable' },
    { blocked: busy, reason: 'request_in_flight' },
    { blocked: projection?.kind === 'invalid', reason: projection?.error?.code ?? 'input_request_stale' },
  ].find(({ blocked }) => blocked);
  return { available: !refusal, reason: refusal?.reason ?? '' };
};

export const matchesInputProjection = (current: ComposerInputProjection, supplied?: ComposerInputProjection) =>
  !supplied || (current.kind === supplied.kind && current.protocol === supplied.protocol
    && current.requestKey === supplied.requestKey && JSON.stringify(current.inputRequest) === JSON.stringify(supplied.inputRequest));
